import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { RawPrompt, useGetRawPromptMutation } from '@/redux/internal-services/agentsApi';
import { useCardCatalogue } from '@/components/SAM/moduleTools';
import { getErrorDetail } from '@/utils/apiError';
import { Alert } from '@/components/UI/Alert';
import { Badge, type BadgeProps } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { EmptyState } from '@/components/UI/EmptyState';
import { Select } from '@/components/UI/Select';
import { AgentFormValues, SectionCard, labelCls, screenMeta } from './agentShared';
import { canonicalModule } from './promptPlacement';
import { humanize } from './screenConditions';

const KIND: Record<string, { label: string; variant: BadgeProps['variant'] }> = {
  global: { label: 'Global', variant: 'accent' },
  general: { label: 'General', variant: 'steel' },
  specific: { label: 'Specific', variant: 'warning' },
};

interface PromptTabProps {
  /** Every module an admin could pick (same list the Modules tab offers). */
  modules: string[];
}

/** The whole prompt on one page, as the admin wrote it. Pick a module (and, inside it, a card) to see what that screen
 *  gets: the global instructions, every module's general blocks and the blocks specific to the selection.
 *  Jinja is shown untouched: `{{ variables }}` and `{% logic %}` are not filled in. */
const PromptTab: React.FC<PromptTabProps> = ({ modules }) => {
  const { control } = useFormContext<AgentFormValues>();
  const blocks = useWatch({ control, name: 'system_prompts' });
  const tools = useWatch({ control, name: 'tools' });
  const placement = useWatch({ control, name: 'tool_scope_overrides' });
  const catalogue = useCardCatalogue();
  const [load, { isLoading }] = useGetRawPromptMutation();

  const [module, setModule] = useState('');
  const [card, setCard] = useState('');
  const [result, setResult] = useState<RawPrompt | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');

  // Cards a user can have open, each under the module it renders in (aliases folded: "properties" is "workflow").
  const cards = useMemo(
    () =>
      (catalogue ?? [])
        .filter((c) => c.status === 'active' && c.enabled && c.permission !== 'workflow:configure')
        .map((c) => ({ id: c.id, module: canonicalModule(c.module), label: humanize(c.id.replace(/[._]/g, ' ')) }))
        .sort((a, b) => a.module.localeCompare(b.module) || a.label.localeCompare(b.label)),
    [catalogue],
  );
  const cardsHere = module ? cards.filter((c) => c.module === module) : cards;

  const pickModule = (next: string) => {
    setModule(next);
    // A card belongs to one module: keep it only while it still does.
    if (card && cards.find((c) => c.id === card)?.module !== next) setCard('');
  };
  const pickCard = (id: string) => {
    setCard(id);
    const owner = cards.find((c) => c.id === id)?.module;
    if (owner) setModule(owner); // choosing a card puts the module on that card's module
  };

  // Re-compose whenever the selection or the blocks change (debounced: typing in a block should not hammer the API).
  const latest = useRef(0);
  useEffect(() => {
    const ticket = ++latest.current;
    const timer = setTimeout(async () => {
      try {
        // Save stamps order = position, so the preview does too: an unsaved move shows in the prompt straight away.
        const ordered = (blocks ?? []).map((b, i) => ({ ...b, order: i }));
        const res = await load({
          system_prompts: ordered,
          tools: tools ?? [],
          tool_scope_overrides: placement ?? undefined,
          module: module || undefined,
          card: module && card ? card : undefined,
        }).unwrap();
        if (ticket !== latest.current) return;
        setResult(res);
        setError('');
      } catch (err) {
        if (ticket !== latest.current) return;
        setError(getErrorDetail(err) ?? 'Could not compose the prompt.');
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [blocks, tools, placement, module, card, load]);

  const copy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(''), 1500);
    } catch {
      setCopied('');
    }
  };

  // The tools as the model receives them: an array of definitions.
  const toolsJson = useMemo(
    () => JSON.stringify((result?.tools ?? []).map((t) => ({ name: t.name, description: t.description, argument_schema: t.argument_schema })), null, 2),
    [result],
  );

  const included = result?.blocks.filter((b) => b.included) ?? [];
  const left = result?.blocks.filter((b) => !b.included) ?? [];
  const scopeLabel = card
    ? `${screenMeta(module).label} › ${cards.find((c) => c.id === card)?.label ?? card}`
    : module
      ? screenMeta(module).label
      : 'no module or card selected';

  return (
    <SectionCard
      title="Prompt preview"
      desc="Everything the model gets on a screen: the prompt in order (the global instructions, every module's general blocks, and the blocks specific to what you pick) and the tools offered there. Shown as written: {{ variables }} and {% logic %} are not filled in."
    >
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="prompt-module">Module</label>
          <Select id="prompt-module" value={module} onChange={(e) => pickModule(e.target.value)}>
            <option value="">None: global and general blocks only</option>
            {modules.map((m) => (
              <option key={m} value={m}>{screenMeta(m).label}</option>
            ))}
          </Select>
        </div>
        <div>
          <label className={labelCls} htmlFor="prompt-card">Card</label>
          <Select id="prompt-card" value={card} onChange={(e) => pickCard(e.target.value)}>
            <option value="">None</option>
            {cardsHere.map((c) => (
              <option key={c.id} value={c.id}>{module ? c.label : `${screenMeta(c.module).label} › ${c.label}`}</option>
            ))}
          </Select>
        </div>
      </div>

      {error && <Alert tone="danger" className="mb-3">{error}</Alert>}

      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-xs text-ui-text3">
          {`For: ${scopeLabel}`}
          {result ? ` · ${included.length} block${included.length === 1 ? '' : 's'} · ${result.char_count.toLocaleString()} chars (about ${Math.round(result.char_count / 4).toLocaleString()} tokens)` : ''}
          {isLoading ? ' · updating…' : ''}
        </span>
        <Button type="button" variant="outline" size="xs" className="ml-auto" onClick={() => copy(result?.prompt ?? '', 'prompt')} disabled={!result?.prompt}>
          {copied === 'prompt' ? 'Copied' : 'Copy'}
        </Button>
      </div>

      {result && !result.prompt ? (
        <EmptyState variant="inline" title="No prompt blocks apply here yet." description="Add blocks in the Instructions, Modules or Cards tabs." />
      ) : (
        <pre
          className="max-h-[60dvh] overflow-auto whitespace-pre-wrap break-words rounded-control border border-ui-border bg-ui-subtle p-3 font-mono text-xs leading-relaxed text-ui-text"
          aria-label="Combined prompt"
          data-testid="combined-prompt"
        >
          {result ? result.prompt : 'Loading…'}
        </pre>
      )}

      {result && (
        <div className="mt-4" data-testid="preview-tools">
          <div className="mb-1 flex items-center gap-2">
            <h3 className="text-sm font-semibold text-ui-text">{`Tools (${result.tools.length})`}</h3>
            <Button type="button" variant="outline" size="xs" className="ml-auto" onClick={() => copy(toolsJson, 'tools')} disabled={result.tools.length === 0}>
              {copied === 'tools' ? 'Copied' : 'Copy'}
            </Button>
          </div>
          {result.tools.length === 0 ? (
            <EmptyState variant="inline" title="No tools are offered on this selection." description="Add tools in the Tools tab and place them in Modules or Cards." />
          ) : (
            <pre
              className="max-h-[60dvh] overflow-auto whitespace-pre-wrap break-words rounded-control border border-ui-border bg-ui-subtle p-3 font-mono text-xs leading-relaxed text-ui-text"
              aria-label="Tools"
              data-testid="tools-array"
            >
              {toolsJson}
            </pre>
          )}
        </div>
      )}

      {result && result.blocks.length > 0 && (
        <details className="mt-4 rounded-control border border-ui-border bg-ui-card p-3 text-xs">
          <summary className="cursor-pointer font-medium text-ui-text">
            {`Blocks: ${included.length} included${left.length ? `, ${left.length} left out for this selection` : ''}`}
          </summary>
          <ul className="mt-2 space-y-1">
            {included.map((b, i) => (
              <li key={`in-${i}`} className="flex flex-wrap items-center gap-2 text-ui-text2">
                <Badge variant={KIND[b.kind].variant}>{KIND[b.kind].label}</Badge>
                <span className="min-w-0 break-words">{b.title}</span>
                {b.roles.length > 0 && <span className="text-ui-text3">{`only for ${b.roles.join(', ')}`}</span>}
              </li>
            ))}
            {left.map((b, i) => (
              <li key={`out-${i}`} className="flex flex-wrap items-center gap-2 text-ui-text3">
                <Badge variant="neutral">Left out</Badge>
                <span className="min-w-0 break-words">{b.title}</span>
                <span>{`(${b.reason})`}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </SectionCard>
  );
};

export default PromptTab;
